import { useMsal, useAccount } from '@azure/msal-react';
import { logger } from './logger';

// Helper function to get current account info
const getCurrentAccount = () => {
    try {
        // This needs to be called within a React component context
        // We'll handle this differently in the actual functions
        return null;
    } catch (error) {
        logger.error('Error getting account:', error);
        return null;
    }
};

// Hook to get account info - use this in React components
export const useCurrentAccount = () => {
    const { instance } = useMsal();
    const account = useAccount(instance.getActiveAccount());
    
    return {
        id: account?.localAccountId || '',
        name: account?.name || 'Unknown User',
        email: account?.username || ''
    };
};

import { PublicClientApplication } from '@azure/msal-browser';

// Helper function to get MSAL instance
const getMsalInstance = () => {
    // Try to get from window if it exists
    if (window.msal && window.msal.instance) {
        return window.msal.instance;
    }
    
    // Try to get from any existing MSAL instances
    if (window.PublicClientApplication) {
        // Look for existing instances in the window object
        for (let key in window) {
            if (window[key] && typeof window[key].getActiveAccount === 'function') {
                return window[key];
            }
        }
    }
    
    return null;
};

// Save survey data function - gets account info internally
const saveSurveyData = async (dataObj, gameId) => {
    // Update this URL to your actual deployed function URL
    const apiUrl = 'https://cosmosdb-web-api.azurewebsites.net/api/httpTrigger_SaveResults';

    const cleanedDataObj = dataObj;
    
    // Get account info from MSAL
    let accountInfo = null;
    
    try {
        const msalInstance = getMsalInstance();
        if (msalInstance) {
            const account = msalInstance.getActiveAccount();
            if (account) {
                logger.log('Found MSAL account:', account); // Debug log
                accountInfo = {
                    id: account.localAccountId || '',
                    name: account.name || 'Unknown User',
                    email: account.username || '' // Email is in username field
                };
            }
        }
        
        // Also try to get from global variables as fallback
        if (!accountInfo && window.MSAccountId) {
            logger.log('Using fallback global variables'); // Debug log
            accountInfo = {
                id: window.MSAccountId,
                name: window.MSAccountName || 'Unknown User',
                email: window.MSAccountEmail || ''
            };
        }
    } catch (error) {
        logger.warn('Could not get account from MSAL:', error);
    }

    // If we still can't get account info, throw error
    if (!accountInfo || !accountInfo.id) {
        logger.error('No account info found. MSAL instance:', getMsalInstance());
        logger.error('Global variables - MSAccountId:', window.MSAccountId, 'MSAccountName:', window.MSAccountName);
        throw new Error('Unable to get account information. Please ensure you are logged in.');
    }

    logger.log('Using account info:', accountInfo); // Debug log

    const dataComplete = {
        data: cleanedDataObj,
        account: accountInfo,
        gameid: gameId
    };

    try {
        logger.log('Sending survey data:', dataComplete);
        
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(dataComplete)
        });

        // Try to parse response as JSON, but handle if it's not JSON
        let result;
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            result = await response.json();
        } else {
            result = await response.text();
        }

        if (response.ok) {
            logger.log('Success! Response status:', response.status);
            logger.log('Response:', result);
            return result;
        } else {
            logger.error('Error status:', response.status);
            logger.error('Error:', result);
            throw new Error(`Failed to save survey data: ${result.error || 'Unknown error'}`);
        }
    } catch (error) {
        logger.error('Exception:', error.message);
        throw error;
    }
};

// Fetch game results function with pagination support
const fetchGameResults = async (gameId, options = {}) => {
    const { page = 1, pageSize = 30, returnFull = false } = options;
    // Build URL with query params
    const params = new URLSearchParams();
    params.append('gameId', gameId);
    if (page) params.append('page', String(page));
    if (pageSize) params.append('pageSize', String(pageSize));

    const apiUrl = `https://cosmosdb-web-api.azurewebsites.net/api/httpTrigger_GetResults?${params.toString()}`;

    try {
        logger.log('Fetching results for gameId:', gameId, 'params:', params.toString());
        
        const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        // Try to parse response as JSON, but handle if it's not JSON
        let result;
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            result = await response.json();
        } else {
            result = await response.text();
        }

        if (response.ok) {
            logger.log('Success! Response status:', response.status);
            logger.log('Found results:', result);
            logger.log(`Page ${result.page || page} size ${result.pageSize} count ${result.count}`);
            if (returnFull) {
                return {
                    results: result.results || [],
                    count: result.count ?? (result.results?.length || 0),
                    page: result.page ?? page,
                    pageSize: result.pageSize ?? pageSize,
                    totalCount: result.totalCount ?? null,
                    totalPages: result.totalPages ?? null,
                    continuationToken: result.continuationToken ?? null,
                };
            }
            return result.results; // Backward compatibility
        } else {
            logger.error('Error status:', response.status);
            logger.error('Error:', result);
            throw new Error(`Failed to fetch game results: ${result.error || 'Unknown error'}`);
        }
    } catch (error) {
        logger.error('Exception:', error.message);
        throw error;
    }
};

export { saveSurveyData, fetchGameResults };